import React from 'react';
import { useSelector } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { ListingBadge } from '../Badge';
import { Link } from 'react-scroll';
import { ListingRating } from './Reviews';
import { t } from '../../../helpers/t';

function NavItem({ id, label, scrollId, scrollProgress }) {
  function onSectionDisplay() {
    document.getElementById(this.id).scrollIntoView();
  }

  return (
    <Link
      onSetActive={onSectionDisplay}
      id={id}
      offset={-160}
      className="p-2 relative text-center cursor-pointer text-sm text-stone-400 hover:text-stone-900 font-normal grow underline-offset-2"
      to={scrollId}
      spy={true}
      activeClass="!text-stone-900 underline"
    >
      {label}
      <motion.div style={{ scaleX: scrollProgress || 0 }} className="absolute bottom-0 left-0 w-full h-[1px] origin-left bg-stone-700" />
    </Link>
  )
}

export function ListingStickyNavbar({ show, scrollProgress }) {
  const { reviews, title } = useSelector((state) => state.listing);

  return (
    <AnimatePresence>
      {
        show && (
          <motion.div initial={{ top: -100 }} animate={{ top: 63 }} exit={{ top: -100 }} className="fixed flex w-full left-0 top-[63px] z-[39] justify-center pointer-events-none">
            <div className="relative listing-navbar-container max-w-6xl w-full flex md:px-6">
              <div className="border-0 border-t border-solid border-gray-300 bg-white drop-shadow md:mt-4 md:rounded-md md:border left pointer-events-auto">
                {/* Title, Badges and Review */}
                <div className="py-2 px-4 flex flex-col md:flex-row">
                  <div className="grow flex">
                    <h1
                      className="mr-2 title text-lg md:text-lg font-medium m-0 text-neutral-900 line-clamp-2 md:line-clamp-1 text-ellipsis"
                      itemProp="name"
                      title={title}>
                      {title}
                    </h1>
                  </div>
                  <div className="flex items-center whitespace-nowrap mt-1 md:mt-0 empty:hidden">
                    <ListingBadge className="mr-1"/>
                    {reviews.length > 0 && (
                      <Link offset={-160} className="flex items-center rounded bg-stone-100 px-2 py cursor-pointer" to="reviews">
                        <ListingRating small/>
                      </Link>
                    )}
                  </div>
                </div>

                {/* Buttons */}
                <div className="overflow-hidden rounded-md">
                  <div className="flex overflow-x-auto whitespace-nowrap border-0 border-t border-solid border-stone-100">
                    <NavItem
                      id="btn-section-ideal-uses"
                      label={t('listings.show.sections.project_types.navbar')}
                      scrollProgress={scrollProgress.uses}
                      scrollId="ideal_uses"
                    />

                    <NavItem
                      id="btn-section-description"
                      label={t('listings.show.sections.description.navbar')}
                      scrollProgress={scrollProgress.description}
                      scrollId="description"
                    />

                    <NavItem
                      id="btn-section-amenities"
                      label={t('listings.show.sections.features.navbar')}
                      scrollProgress={scrollProgress.amenities}
                      scrollId="amenities"
                    />

                    <NavItem
                      id="btn-section-reviews"
                      label={t('listings.show.sections.reviews.navbar')}
                      scrollProgress={scrollProgress.reviews}
                      scrollId="reviews"
                    />

                    <NavItem
                      id="btn-section-photos"
                      label={t('listings.show.sections.photos.navbar')}
                      scrollProgress={scrollProgress.photos}
                      scrollId="photos"
                    />

                    <NavItem
                      id="btn-section-availabilities"
                      label={t('listings.show.sections.availability.navbar')}
                      scrollProgress={scrollProgress.availabilities}
                      scrollId="availabilities"
                    />

                    <NavItem
                      id="btn-section-location"
                      label={t('listings.show.sections.map.navbar')}
                      scrollProgress={scrollProgress.location}
                      scrollId="location"
                    />

                  </div>
                </div>
              </div>
              <div className="right" />
            </div>
          </motion.div>
        )
      }
    </AnimatePresence>
  );
}
